import { createApiRef } from "@backstage/core";
import { DiscoveryApi, IdentityApi } from "@backstage/core-plugin-api";
import axios from "axios";

export const honeybadgerApiRef = createApiRef<HoneybadgerApi>({
  id: "plugin.honeybadger-api.service",
  description: "Used by plugins to make requests to honeybadger",
});

type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
};

export interface GetFaultsOptions {
  projectKey: string;
  query?: string;
  sortBy?: SORT_OPTIONS;
  limit?: string;
}

export const DEFAULT_API_LIMIT = "25";

export enum SORT_OPTIONS {
  RECENT = "recent",
  FREQUENT = "frequent",
}

export class HoneybadgerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  /** Retrieve the primary URL  */
  private async getUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl("honeybadger");
  }

  public async getProjectByKey({ projectKey }: { projectKey: string }) {
    try {
      const { token } = await this.identityApi.getCredentials();
      const resp = await axios.post(
        `${await this.getUrl()}/getProjectByKey`,
        { projectKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = resp.data.data;
      return result;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  public async getFaults({
    projectKey,
    query,
    sortBy = SORT_OPTIONS.RECENT,
    limit = DEFAULT_API_LIMIT,
  }: GetFaultsOptions) {
    try {
      const { token } = await this.identityApi.getCredentials();
      const resp = await axios.post(
        `${await this.getUrl()}/faults`,
        { projectKey, query, sortBy, limit },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (resp.data.status === "error") {
        throw new Error(resp.data.error);
      }
      return resp.data.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  public async getNoticesByLocation({
    projectKey,
  }: {
    projectKey: string;
  }): Promise<{ data: [string, number][] }> {
    try {
      const { token } = await this.identityApi.getCredentials();
      const { data } = await axios.get(
        `${await this.getUrl()}/getNoticesByLocation?projectKey=${projectKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.status === "error") {
        throw new Error(data.error);
      }
      return data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
}
